import { ReactNode, useMemo } from "react";
import { usePortal } from "@/data/portal/context";
import { buildPortalCSSVariables } from "@/data/portal/engine";
import { HelpCircle } from "lucide-react";

/**
 * White-labeled portal layout that dynamically applies tenant branding.
 * Consumes PortalContext — must be rendered inside PortalProvider.
 */
export function BrandedPortalLayout({ children }: { children: ReactNode }) {
  const { branding, hasFeature } = usePortal();

  const cssVars = useMemo(
    () => (branding ? buildPortalCSSVariables(branding) : {}),
    [branding]
  );

  const companyName = branding?.companyName || "Your Company";
  const initials = companyName.split(" ").map(w => w[0]).join("").slice(0, 3);
  const tagline = branding?.tagline || "";

  return (
    <div className="min-h-screen bg-background" style={cssVars as React.CSSProperties}>
      {/* ── Branded Header ── */}
      <header className="sticky top-0 z-50 bg-card border-b border-border/60 shadow-sm">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            {branding?.logoUrl ? (
              <img
                src={branding.logoUrl}
                alt={companyName}
                className="h-9 w-9 rounded-lg object-contain"
              />
            ) : (
              <div
                className="h-9 w-9 rounded-lg flex items-center justify-center"
                style={{ background: `hsl(${cssVars["--portal-primary"] || "207 80% 42%"})` }}
              >
                <span className="text-white font-bold text-sm">{initials}</span>
              </div>
            )}
            <div>
              <h1 className="text-sm font-bold tracking-tight text-foreground">
                {companyName}
              </h1>
              {tagline && (
                <p className="text-[10px] text-muted-foreground">{tagline}</p>
              )}
            </div>
          </div>

          {branding?.supportEmail && (
            <a
              href={`mailto:${branding.supportEmail}`}
              className="text-xs hover:underline inline-flex items-center gap-1"
              style={{ color: `hsl(${cssVars["--portal-primary"] || "207 80% 42%"})` }}
            >
              <HelpCircle className="h-3.5 w-3.5" /> Need Help?
            </a>
          )}
        </div>
      </header>

      {/* ── Main Content ── */}
      <main className="max-w-4xl mx-auto px-4 sm:px-6 py-6">
        {children}
      </main>

      {/* ── Footer ── */}
      <footer className="border-t border-border/40 mt-12">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-4">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-2">
            <p className="text-[10px] text-muted-foreground">
              © {new Date().getFullYear()} {companyName}
            </p>
            <div className="flex items-center gap-4">
              {branding?.supportPhone && (
                <a href={`tel:${branding.supportPhone}`} className="text-[10px] text-muted-foreground hover:text-foreground">
                  {branding.supportPhone}
                </a>
              )}
              {branding?.supportEmail && (
                <a href={`mailto:${branding.supportEmail}`} className="text-[10px] text-muted-foreground hover:text-foreground">
                  {branding.supportEmail}
                </a>
              )}
            </div>
          </div>
          <p className="text-[9px] text-muted-foreground/60 text-center mt-2">
            Powered by Koredor
          </p>
        </div>
      </footer>
    </div>
  );
}
